import React, { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import moment from 'moment';
import { IIncident } from '../../../../pages/Incidents/interfaces';
import { ModalActions } from '../../../KubitModal/KubitModalUI';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import * as FormComponents from '../../../Form';
import { DescriptionField, NameField } from '../../../Form';
import {
  EVENT_NAME_MAX_LENGTH,
  INTERNAL_DATE_FORMAT,
} from '../../../../config/constants';
import {
  IIncidentCreateInput,
  IIncidentEditInput,
} from '../../../../utils/apollo/graphqlSchema';
import {
  descriptionFieldSchema,
  nameFieldSchema,
} from '../../../../utils/validation';
import useManipulateIncident from './useManipulateIncident';
import * as IncidentCreateModalUI from './IncidentCreateModalUI';
import IncidentTypeMenu from '../../IncidentTypeMenu';
import { parseIncidentDescription } from '../../utils';
import { globalFormat } from '../../../../utils/formatting';
import useSyndicateTimezoneLabel from '../../../../utils/hooks/useSyndicateTimezoneLabel';
import { DatePicker } from '../../../DatePicker';

interface IProps {
  selectedStartDate: Date;
  onClose?: () => void;
  selectedEndDate?: Date;
  currentIncident?: IIncident;
  analysisId?: string;
  startDateReadOnly?: boolean;
  stopMovementPropagation?: boolean;
  closeOnScroll?: boolean;
}

const validationSchema = z.object({
  startDate: z.date(),
  endDate: z.date(),
  typeId: z.string().min(1),
  name: nameFieldSchema(EVENT_NAME_MAX_LENGTH),
  description: descriptionFieldSchema,
});

type ValidationSchemaType = z.infer<typeof validationSchema>;

export default ({
  onClose,
  selectedStartDate,
  selectedEndDate,
  currentIncident,
  analysisId,
  startDateReadOnly,
  stopMovementPropagation,
  closeOnScroll,
}: IProps) => {
  const defaultEndDate = selectedEndDate || selectedStartDate;
  const shortTimeZone = useSyndicateTimezoneLabel();
  const [startDate, setStartDate] = useState<Date>(selectedStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);
  const [typeId, setTypeId] = useState(currentIncident?.type?.id ?? '');
  const [name, setName] = useState(currentIncident?.name ?? '');

  const [descriptionString, setDescriptionString] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const safeParse = validationSchema.safeParse({
    startDate,
    endDate,
    typeId,
    name,
    description: descriptionString,
  });

  const isFormValid = safeParse.success;

  const errors = !isFormValid ? safeParse?.error?.format() : null;

  const {
    createIncident,
    isCreateIncidentLoading,
    editIncident,
    isEditIncidentLoading,
  } = useManipulateIncident(analysisId);

  const handleChangeStartDate = useCallback(
    (date: Date | null) => {
      if (!date || startDateReadOnly) {
        return;
      }

      setStartDate(date);

      if (moment(date).isAfter(moment(endDate))) {
        setEndDate(date);
      }
    },
    [startDateReadOnly, endDate],
  );

  const handleChangeEndDate = useCallback((date: Date | null) => {
    if (!date) {
      return;
    }

    setEndDate(date);
  }, []);

  const prepareMutationInput = (values: ValidationSchemaType) => ({
    start: globalFormat.formatDate(values.startDate, INTERNAL_DATE_FORMAT),
    end: globalFormat.formatDate(values.endDate, INTERNAL_DATE_FORMAT),
    name: values.name,
    typeId: values.typeId,
    description: values?.description ?? '',
  });

  const handleCreate = async () => {
    setSubmitting(true);

    if (!isFormValid) {
      return;
    }

    const input: IIncidentCreateInput = prepareMutationInput(safeParse.data);

    const { data } = await createIncident({
      variables: {
        input,
      },
    });

    if (onClose && data?.incidentCreate?.entity?.id) {
      onClose();
    }
  };

  const handleUpdate = async () => {
    setSubmitting(true);

    if (!currentIncident?.id || !isFormValid) {
      return;
    }

    const mutationInput = prepareMutationInput(safeParse.data);

    const input: IIncidentEditInput = {
      ...mutationInput,
      incidentId: currentIncident.id,
    };

    const { data } = await editIncident({
      variables: {
        input,
      },
    });

    if (onClose && data?.incidentEdit?.entity?.id) {
      onClose();
    }
  };

  const onMouseMoveInContainer = useCallback(
    (mouseEvent: any) =>
      stopMovementPropagation ? mouseEvent.stopPropagation() : null,
    [stopMovementPropagation],
  );
  // Handle correct description value
  useEffect(() => {
    if (!currentIncident?.description) {
      return;
    }

    setDescriptionString(parseIncidentDescription(currentIncident.description));
  }, [currentIncident]);

  useEffect(() => {
    if (!closeOnScroll) {
      return;
    }

    const mainContainer = document.getElementById('main-container');

    if (closeOnScroll && onClose) {
      mainContainer?.addEventListener('scroll', onClose);
    }
    return () => {
      if (closeOnScroll && onClose) {
        mainContainer?.removeEventListener('scroll', onClose);
      }
    };
  }, [closeOnScroll, onClose]);

  return (
    <IncidentCreateModalUI.Container onMouseMove={onMouseMoveInContainer}>
      <IncidentCreateModalUI.Header>
        {currentIncident?.name ?? 'Create an Incident'}
      </IncidentCreateModalUI.Header>
      <IncidentCreateModalUI.Body>
        <IncidentCreateModalUI.DateRangeContainer>
          <FormComponents.VerticalField>
            <FormComponents.FieldLabel>Start</FormComponents.FieldLabel>
            <DatePicker
              selectedValue={startDate}
              onChange={handleChangeStartDate}
              disabled={startDateReadOnly}
            />
          </FormComponents.VerticalField>
          <FormComponents.VerticalField>
            <FormComponents.FieldLabel>End</FormComponents.FieldLabel>
            <DatePicker
              selectedValue={endDate}
              onChange={handleChangeEndDate}
              minDate={startDate}
            />
          </FormComponents.VerticalField>
          <IncidentCreateModalUI.TimeZoneBadge color="grey" size="primary">
            {shortTimeZone}
          </IncidentCreateModalUI.TimeZoneBadge>
        </IncidentCreateModalUI.DateRangeContainer>
        <FormComponents.VerticalField>
          <FormComponents.FieldLabel>Incident Type</FormComponents.FieldLabel>
          <IncidentTypeMenu
            value={typeId}
            onChange={setTypeId}
            hasError={submitting && Boolean(errors?.typeId)}
          />
        </FormComponents.VerticalField>
        <NameField
          label={'Incident Name'}
          value={name}
          onChange={setName}
          targetErrorElement={'Incident'}
          maxLength={EVENT_NAME_MAX_LENGTH}
          placeholder={'Add incident name here'}
          hasError={submitting && Boolean(errors?.name)}
        />
        <DescriptionField
          label={'Incident Description'}
          value={descriptionString}
          onChange={setDescriptionString}
          targetErrorElement={'Incident'}
          placeholder={'Add incident description here'}
        />
        <ModalActions>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={isCreateIncidentLoading || isEditIncidentLoading}
            trackContext="incidents"
            trackAction="cancel"
          >
            Cancel
          </SecondaryButton>
          {currentIncident?.id ? (
            <PrimaryButton
              type="button"
              onClick={handleUpdate}
              isGreyedOut={!isFormValid}
              disabled={isEditIncidentLoading}
              trackContext="incidents"
              trackAction="update"
            >
              Update
            </PrimaryButton>
          ) : (
            <PrimaryButton
              type="button"
              onClick={handleCreate}
              isGreyedOut={!isFormValid}
              disabled={isCreateIncidentLoading}
              trackContext="incidents"
              trackAction="create"
            >
              Create
            </PrimaryButton>
          )}
        </ModalActions>
      </IncidentCreateModalUI.Body>
    </IncidentCreateModalUI.Container>
  );
};
